import React, { FC, useState, useEffect } from 'react'
// @ts-ignore
import { HashLink } from 'react-router-hash-link'
// @ts-ignore
import { BrowserRouter } from 'react-router-dom'
import { HeaderProps, MenuItems } from './Header.types'
import './Header.scss'
import {
  APIENDPOINT,
  getCWPEnvironmentForURL,
  sanitizeHTML,
  utilizeScroll,
} from '../../utils/common-utils'
import { useFetch, useOneTrustBundles, usePrivacyOptOutCheck } from '../../utils/hooks'

export const Header: FC<HeaderProps> = props => {
  const { content } = props
  const {
    year,
    pdfFilePath,
    mailSubject,
    mailBody,
    menuName,
    emailToolTipImage,
    downloadToolTipImage,
    ampLogo,
    expandLogo,
    yearsImagePath,
  } = content

  const [showSubMenu, setShowSubMenu] = useState(false)
  const showOptOut = usePrivacyOptOutCheck()
  const { oneTrustLabel, oneTrustUrl } = useOneTrustBundles()

  useEffect(() => {
    const scrollEvent = () => {
      if (window.scrollY > 0) {
        document.body.classList.add('scrolling')
      } else {
        document.body.classList.remove('scrolling')
      }
    }
    //add scrolling to body, for top navigation animation
    //using css3 animations
    window.addEventListener('scroll', scrollEvent)

    return () => window.removeEventListener('scroll', scrollEvent)
  }, [])

  useEffect(() => {
    //adds disclaimer for california users on annual reports footer
    const footerNode = document.getElementById('report-footer')
    const californiaDisclaimerContainer = footerNode
      ?.querySelector('a[href*="ameriprise"]')
      ?.closest('p')
    const californiaDisclaimer =
      showOptOut && oneTrustLabel
        ? ` | <a href=${sanitizeHTML(
            oneTrustUrl
          )} target='_blank' rel='noopener noreferrer'>${oneTrustLabel}</a>`
        : null

    if (californiaDisclaimerContainer && californiaDisclaimer) {
      californiaDisclaimerContainer.insertAdjacentHTML('beforeend', californiaDisclaimer)
    }
  }, [showOptOut, oneTrustLabel, oneTrustUrl])

  const env = getCWPEnvironmentForURL(window?.location.href)
  const baseGalleryPath = APIENDPOINT(env).baseGallery
  const emailToolTipImageUrl = `${baseGalleryPath}${emailToolTipImage}`
  const downloadToolTipImageUrl = `${baseGalleryPath}${downloadToolTipImage}`
  const ampLogoUrl = `${baseGalleryPath}${ampLogo}`
  const expandLogoUrl = `${baseGalleryPath}${expandLogo}`
  const yearsImageUrl = `${baseGalleryPath}${yearsImagePath}`

  const annualReportMenuUrl = `${APIENDPOINT(env).menuBaseWithParams}${
    menuName || 'annual-report-menu'
  }`
  const annualReportMenu = useFetch(annualReportMenuUrl)

  const { status: menuStatus, data: annualReportMenuData } = annualReportMenu
  const menuItems = annualReportMenuData?.menuItems as MenuItems

  const handleMouseEnter = () => {
    setShowSubMenu(true)
  }

  const handleMouseLeave = () => {
    setShowSubMenu(false)
  }

  const renderNavBar = (menuItems: MenuItems) => {
    if (menuItems.length === 0) {
      return
    }

    return (
      <ul className='List List--inline u-border0'>
        {menuItems.map(({ HstMenuItemRepresentation }) => {
          return (
            <>
              {!HstMenuItemRepresentation.menuItems.length ? (
                <li
                  className='List-item Navigation-Link u-textCenter u-textUppercase u-marginRight8'
                  key={HstMenuItemRepresentation.name}>
                  <HashLink
                    onMouseEnter={handleMouseLeave}
                    scroll={() => utilizeScroll(HstMenuItemRepresentation.url, 100)}
                    to={HstMenuItemRepresentation.url}
                    className='u-textSizeMsrm1 u-textColorPurple'>
                    {HstMenuItemRepresentation.name}
                  </HashLink>
                </li>
              ) : (
                <li
                  className='List-item Navigation-Link u-textCenter u-textUppercase u-paddingHoriz8'
                  key={HstMenuItemRepresentation.name}
                  onMouseEnter={handleMouseEnter}>
                  <HashLink
                    scroll={() => utilizeScroll(HstMenuItemRepresentation.url, 100)}
                    to={HstMenuItemRepresentation.url}
                    onClick={handleMouseLeave}
                    className='u-textSizeMsrm1 u-textColorPurple'>
                    {HstMenuItemRepresentation.name}
                    <img src={expandLogoUrl} className='u-paddingLeft4' alt='expand-icon' />
                  </HashLink>
                  {showSubMenu && (
                    <div className='submenu' onMouseLeave={handleMouseLeave}>
                      <ul>
                        {HstMenuItemRepresentation.menuItems.map(
                          ({ HstMenuItemRepresentation: submenuItem }) => {
                            return (
                              <li
                                className='u-paddingVert16 u-paddingHoriz16  u-textLeft u-textUppercase u-md-textTight'
                                key={submenuItem.name}>
                                <HashLink
                                  scroll={() => utilizeScroll(submenuItem.url, 100)}
                                  to={submenuItem.url}
                                  onClick={handleMouseLeave}
                                  className='u-textSizeMsrm1 u-textColorPurple'>
                                  {submenuItem.name}
                                </HashLink>
                              </li>
                            )
                          }
                        )}
                      </ul>
                    </div>
                  )}
                </li>
              )}
            </>
          )
        })}
      </ul>
    )
  }

  const renderIcons = () => {
    return (
      <div className='action-icons u-marginTop16 u-flex u-sizeFull u-flexJustifyCenter u-flexAlignItemsCenter u-sm-flexCol u-sm-paddingRight24'>
        <span className='u-textColorBrand u-textCenter u-textSizeMsrp3 u-sm-textSizeMsr u-paddingRight20 u-sm-paddingRight0 u-textSecondary u-borderRight u-sm-borderRight0'>
          Annual Report <span className='action-icons-heading-year'>{year}</span>
        </span>
        <div className='u-flex u-flexJustifyCenter u-paddingLeft20 u-sm-paddingLeft0'>
          <a
            href={`mailto:?subject=${mailSubject}&body=${mailBody}`}
            aria-label={`Share ${year} Annual Report via email`}
            className='adel-material-symbols-24 u-textColorMidnightBlue share email u-marginRight8'>
            mail <img src={emailToolTipImageUrl} alt={`Share ${year} Annual Report via email`} />
          </a>
          <a
            href={pdfFilePath}
            aria-label={`Download ${year} Annual Report as PDF`}
            className='share download adel-material-symbols-24 u-textColorMidnightBlue'
            rel='external'
            target='_blank'>
            download{' '}
            <img src={downloadToolTipImageUrl} alt={`Download ${year} Annual Report as PDF`} />
          </a>
        </div>
      </div>
    )
  }

  return (
    <BrowserRouter>
      <header className='annual-report-header'>
        <div className='report-header'>
          <section className='u-sm-hidden'>{renderIcons()}</section>
          <div className='nav-logo-container u-flex u-sm-flexJustifyBetween u-flexAlignItemsCenter'>
            <div className='u-flex u-flexJustifyAround u-flexAlignItemsCenter u-sizeFull u-sm-sizeFit'>
              <div className='u-flex u-flexJustifyCenter u-size2of12 u-md-size2of12 u-sm-sizeFit'>
                <div className='logo amp-logo u-paddingVert20 u-sm-marginRight8 u-sm-paddingLeft20'>
                  <a href='https://www.ameriprise.com'>
                    <img src={ampLogoUrl} alt='Ameriprise Financial logo' />
                  </a>
                </div>
              </div>
              <div className={`u-size8of12 u-md-size6of12 u-sm-hidden`}>
                <div className='navigation u-sm-hidden'>
                  <nav role='navigation' className='u-md-hidden'>
                    {menuStatus === 'fetched' && menuItems?.length ? renderNavBar(menuItems) : null}
                  </nav>
                </div>
              </div>
              <div className='u-size2of12 u-md-size2of12 u-sm-sizeFit u-sm-hidden u-flex u-flexJustifyCenter'>
                <div className='logo logo--years u-paddingVert20 u-sm-paddingVert0 u-sm-marginVert20 u-sm-paddingLeft8 u-sm-borderLeft'>
                  <img src={yearsImageUrl} alt='130 years img' />
                </div>
              </div>
            </div>

            <div className='u-sm-block u-hidden u-sm-sizeFit'>{renderIcons()}</div>
          </div>
        </div>
      </header>
    </BrowserRouter>
  )
}
